<template>
  <span v-if="isNotApplicable">-</span>
  <span v-else v-tooltip="tooltip">
    <WdsLabel
      xs
      subtle
      :title="tooltip ? '' : undefined"
      :success="isSuccess"
      :warning="isWarning"
      :error="isError"
    >
      {{ statusText }}
    </WdsLabel>
  </span>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsLabel } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useTranslation } from '@/composables/translation'
import { PaymentRequestStatus, PaymentRequestWorkerPayoutStatus } from '@/gql/graphql'

const PaymentRequestWorkerPayoutStatusColumn = graphql(`
  fragment PaymentRequestWorkerPayoutStatusColumn on PaymentRequest {
    id
    workerPayoutStatus
    status
    dueAt
  }
`)

const props = defineProps<{
  row: FragmentType<typeof PaymentRequestWorkerPayoutStatusColumn>
}>()

const { $t } = useTranslation()
const column = computed(() => useFragment(PaymentRequestWorkerPayoutStatusColumn, props.row))

const status = computed(() => column.value.workerPayoutStatus)
const approvalStatus = computed(() => column.value.status)

const isNotApplicable = computed(() => approvalStatus.value !== PaymentRequestStatus.Approved)

const statusText = computed(() => {
  switch (status.value) {
    case PaymentRequestWorkerPayoutStatus.Paid:
      return $t('Paid')
    case PaymentRequestWorkerPayoutStatus.Prepaid:
      return $t('Prepaid')
    case PaymentRequestWorkerPayoutStatus.Overdue:
      return $t('Overdue')
    case PaymentRequestWorkerPayoutStatus.Due:
      return $t('Due')
    case PaymentRequestWorkerPayoutStatus.Unpaid:
    default:
      return $t('Unpaid')
  }
})

const daysOverdue = computed(() => {
  if (status.value !== PaymentRequestWorkerPayoutStatus.Overdue || !column.value.dueAt) {
    return 0
  }
  const dueDate = new Date(column.value.dueAt)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffTime = Math.abs(today.getTime() - dueDate.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
})

const tooltip = computed(() => {
  switch (status.value) {
    case PaymentRequestWorkerPayoutStatus.Prepaid:
      return `${$t('The freelancer has been paid in advance.')} ${$t('The final payment on the invoice is still due.')}`
    case PaymentRequestWorkerPayoutStatus.Overdue:
      return $t('This payment request is :days days overdue.', { days: daysOverdue.value })
    case PaymentRequestWorkerPayoutStatus.Due:
      if (column.value.dueAt) {
        const dueDate = new Date(column.value.dueAt)
        return $t('This payment request is due on :date.', {
          date: dueDate.toISOString().split('T')[0],
        })
      }
      return null
    default:
      return null
  }
})

const isSuccess = computed(
  () =>
    status.value === PaymentRequestWorkerPayoutStatus.Paid ||
    status.value === PaymentRequestWorkerPayoutStatus.Prepaid,
)
const isWarning = computed(
  () =>
    status.value === PaymentRequestWorkerPayoutStatus.Due ||
    status.value === PaymentRequestWorkerPayoutStatus.Unpaid,
)
const isError = computed(() => status.value === PaymentRequestWorkerPayoutStatus.Overdue)
</script>
